import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpBaseService } from 'src/app/base/http-base.service';
import ModuleActions from '../../actions/generations/module.actions';
import { catchError, EMPTY, finalize, map, of, switchMap, tap } from 'rxjs';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from 'src/app/services/util.service';

@Injectable()
export class ModuleEffects {
  private readonly actions = inject(Actions);
  private readonly httpService = inject(HttpBaseService);
  private readonly utilService = inject(UtilService);
  private readonly loaderService = inject(AppLoaderService);
  private readonly translateService = inject(TranslateService);

  getAllModules$ = createEffect(() =>
    this.actions.pipe(
      ofType(ModuleActions.getAllModules),
      tap((action: any) => {
        if (action.showLoader !== false) {
          this.loaderService.show();
        }
      }),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => ModuleActions.setModules({ data: response })),
          finalize(() => {
            if (action.showLoader !== false) {
              this.loaderService.hide();
            }
          })
          // Optionally handle errors here with catchError if needed
        );
      })
    )
  );

  createModule$ = createEffect(() =>
    this.actions.pipe(
      ofType(ModuleActions.createModule),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((response: any) => {
            this.utilService.showSuccess(
              this.translateService.instant('COMPONENT_ADDED_SUCCESSFULLY', { component: 'Module' })
            );
            return ModuleActions.createModuleSuccess(response);
          }),
          finalize(() => {
            this.loaderService.hide();
          }),
          // Optionally handle errors here with catchError if needed
          catchError((error) => {
            return of(ModuleActions.createModuleFailure({ error }));
          })
        );
      })
    )
  );

  createModuleSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(ModuleActions.createModuleSuccess),
      map((response: any) => ModuleActions.saveModule({ data: response }))
    )
  );

  createModuleFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ModuleActions.createModuleFailure),
        map((error: any) => {
          console.error('Error creating module2:', error);
        })
      ),
    { dispatch: false }
  );

  updateModule$ = createEffect(() =>
    this.actions.pipe(
      ofType(ModuleActions.updateModule),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((response: any) => ModuleActions.updateModuleSuccess(response)),
          finalize(() => {
            this.loaderService.hide();
          }),
          // Optionally handle errors here with catchError if needed
          catchError((error) => {
            console.error('Error updating module:', error);
            return EMPTY; // <-- completes the stream without dispatching
          })
        );
      })
    )
  );

  updateModuleSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(ModuleActions.updateModuleSuccess),
      map((response: any) => {
        this.utilService.showSuccess(
          this.translateService.instant('COMPONENT_UPDATED_SUCCESSFULLY', { component: 'Module' })
        );
        return ModuleActions.saveModule({ data: response });
      })
    )
  );

  deleteModule$ = createEffect(() =>
    this.actions.pipe(
      ofType(ModuleActions.deleteModule),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: ReturnType<typeof ModuleActions.deleteModule>) => {
        const { apiConstants, params } = action;
        return this.httpService[apiConstants.method](apiConstants, params).pipe(
          map((response: any) => ModuleActions.deleteModuleSuccess({ componentId: params.moduleId })),
          finalize(() => {
            this.loaderService.hide();
          }),
          catchError((error) => {
            console.log('Error deleting module:', error);
            return of(ModuleActions.deleteModuleFailure());
          })
        );
      })
    )
  );

  deleteModuleSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ModuleActions.deleteModuleSuccess),
        map(({ componentId: _componentId }) => {
          this.utilService.showSuccess(
            this.translateService.instant('COMPONENT_DELETED_SUCCESSFULLY', { component: 'Module' }) as string
          );
          return EMPTY; // No further action to dispatch
        })
      ),
    { dispatch: false }
  );

  deleteModuleFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(ModuleActions.deleteModuleFailure),
        map(() => {
          return EMPTY; // No further action to dispatch
        })
      ),
    { dispatch: false }
  );
}
